export type InvoiceTableItem = {
  id: string; opportunity_id: string; name: string; client_name: string;
  issue_date: string; total_amount: number; paid_amount: number; invoice_status: string;
  line_items?: { payment_type: string; parcela_num_on_invoice?: number | null; installments_count?: number | null }[];
};
export type InvoiceOpportunityRow<T extends InvoiceTableItem> = {
  id: string; name: string; client_name: string; owner_name: string; issue_date: string;
  total_amount: number; paid_amount: number; installment_count: number; statuses: Record<string, number>;
  invoice?: T; subRows?: InvoiceOpportunityRow<T>[];
};
export function invoiceOpportunityRows<T extends InvoiceTableItem>(invoices: T[], owners: Map<string, string>, allInvoices: T[] = invoices): InvoiceOpportunityRow<T>[] {
  const groups = new Map<string, InvoiceOpportunityRow<T>>();
  const counts = new Map<string, number>();
  for (const invoice of allInvoices) {
    const key = invoice.opportunity_id || invoice.id;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  for (const invoice of invoices) {
    const key = invoice.opportunity_id || invoice.id;
    let group = groups.get(key);
    if (!group) {
      group = { id: `opportunity:${key}`, name: invoice.name, client_name: '', owner_name: owners.get(invoice.opportunity_id) || '', issue_date: invoice.issue_date, total_amount: 0, paid_amount: 0, installment_count: counts.get(key) || 0, statuses: {}, subRows: [] };
      groups.set(key, group);
    }
    group.subRows!.push({ id: invoice.id, name: invoice.name, client_name: invoice.client_name, owner_name: group.owner_name, issue_date: invoice.issue_date, total_amount: invoice.total_amount, paid_amount: invoice.paid_amount, installment_count: 1, statuses: { [invoice.invoice_status]: 1 }, invoice });
    group.total_amount += invoice.total_amount;
    group.paid_amount += invoice.paid_amount;
    group.statuses[invoice.invoice_status] = (group.statuses[invoice.invoice_status] || 0) + 1;
  }
  for (const group of groups.values()) {
    group.subRows!.sort((a,b) => {
      const aSingle=a.invoice && invoicePaymentKind(a.invoice)==='single';
      const bSingle=b.invoice && invoicePaymentKind(b.invoice)==='single';
      return Number(!aSingle)-Number(!bSingle) || a.issue_date.localeCompare(b.issue_date) || a.id.localeCompare(b.id);
    });
    group.client_name = Array.from(new Set(group.subRows!.map(row => row.client_name).filter(Boolean))).join(', ');
    group.issue_date = group.subRows!.reduce((first,row)=>row.issue_date<first?row.issue_date:first,group.subRows![0].issue_date);
  }
  return Array.from(groups.values());
}

/** Number the complete opportunity schedule before applying month/client/status filters. */
export function invoiceInstallmentNumbers<T extends Pick<InvoiceTableItem, 'id' | 'opportunity_id' | 'issue_date'> & Pick<InvoiceTableItem, 'line_items'>>(invoices: T[]): Record<string, { num: number; total: number }> {
  const groups = new Map<string, T[]>();
  const numbers: Record<string, { num: number; total: number }> = {};
  for (const invoice of invoices) {
    if (invoicePaymentKind(invoice) === 'single') {
      numbers[invoice.id] = { num: 1, total: 1 };
      continue;
    }
    const key = invoice.opportunity_id || invoice.id;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(invoice);
  }
  for (const group of groups.values()) {
    group.sort((a,b) => a.issue_date.localeCompare(b.issue_date) || a.id.localeCompare(b.id));
    group.forEach((invoice,index) => { numbers[invoice.id] = { num: index + 1, total: group.length }; });
  }
  // Prefer the payment plan's actual installment position when all lines agree.
  for (const invoice of invoices) {
    const parts = (invoice.line_items || []).filter(item => item.payment_type === 'parcelado');
    const first = parts[0];
    if (first && Number(first.parcela_num_on_invoice) > 0 && Number(first.installments_count) >= Number(first.parcela_num_on_invoice)
      && parts.every(item => item.parcela_num_on_invoice === first.parcela_num_on_invoice && item.installments_count === first.installments_count)) {
      numbers[invoice.id] = { num: Number(first.parcela_num_on_invoice), total: Number(first.installments_count) };
    }
  }
  return numbers;
}

export function invoicePaymentKind(invoice: Pick<InvoiceTableItem, 'line_items'>): 'single' | 'installment' | 'mixed' | 'invoice' {
  const methods = new Set(invoice.line_items?.map(item=>item.payment_type) || []);
  const single=methods.has('avista');
  const installment=methods.has('parcelado');
  return single&&installment?'mixed':single?'single':installment?'installment':'invoice';
}
